import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Space } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  UserOutlined,
  TeamOutlined,
  DashboardOutlined,
  FundProjectionScreenOutlined,
  ApartmentOutlined,
  ClusterOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  LogoutOutlined,
  SolutionOutlined,
  DownOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  FileTextOutlined,
  ToolOutlined,
  FileSearchOutlined,
  MessageOutlined
} from '@ant-design/icons';
import api from '../api';

const { Header, Sider, Content } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Fetch user info to determine role
    const fetchUserInfo = async () => {
        try {
            const response = await api.get('/auth/me'); // Assuming an endpoint exists
            setUserRole(response.data.role);
            setUserName(response.data.name);
        } catch (error) {
            // If failed to get user info, maybe try to decode token or just default
            console.error("Failed to fetch user info", error);
        }
    };
    fetchUserInfo();
  }, []);

  // Define all possible menu items
  const allMenuItems = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '数据概览', roles: ['ADMIN', 'TEACHING_ADMIN'] },
    { key: '/data-screen', icon: <FundProjectionScreenOutlined />, label: '数据大屏', roles: ['LOGISTICS_ADMIN'] },
    { key: '/accommodation', icon: <ApartmentOutlined />, label: '住宿学员管理', roles: ['LOGISTICS_ADMIN'] },
    { key: '/dormitories', icon: <ClusterOutlined />, label: '寝室管理', roles: ['LOGISTICS_ADMIN'] },
    { key: '/dormitory-inspection', icon: <CheckCircleOutlined />, label: '查寝', roles: ['LOGISTICS_ADMIN'] },
    { key: '/students', icon: <TeamOutlined />, label: '学员管理', roles: ['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN'] },
    { key: '/teachers', icon: <UserOutlined />, label: '教师管理', roles: ['ADMIN'] },
    { key: '/classes', icon: <SolutionOutlined />, label: '班级管理', roles: ['ADMIN', 'TEACHING_ADMIN'] },
    { key: '/attendance', icon: <HistoryOutlined />, label: '签到管理', roles: ['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN'] },
    { key: '/attendance/history', icon: <HistoryOutlined />, label: '历史签到', roles: ['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN'] },
    { key: '/leave', icon: <FileTextOutlined />, label: '请假管理', roles: ['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN'] },
    { key: '/interview', icon: <MessageOutlined />, label: '约谈管理', roles: ['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN'] },
    { key: '/repair', icon: <ToolOutlined />, label: '报修管理', roles: ['ADMIN', 'LOGISTICS_ADMIN', 'LOGISTICS_TEACHER'] },
    { key: '/logs', icon: <FileSearchOutlined />, label: '操作日志', roles: ['ADMIN'] },
  ];

  // Filter menu items based on role
  const menuItems = allMenuItems.filter(item => {
      if (!userRole) return false; // Don't show until role is loaded
      return item.roles.includes(userRole);
  });

  const handleMenuClick = (e) => {
    navigate(e.key);
  };
  
  const handleLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      navigate('/login');
  };


  const userMenuItems = [
    {
      key: 'logout',
      label: '退出登录',
      icon: <LogoutOutlined />,
      onClick: handleLogout
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed} 
        theme="light"
        breakpoint="md"
        collapsedWidth={0}
        onBreakpoint={(broken) => setCollapsed(broken)}
        style={{
          boxShadow: '2px 0 8px 0 rgba(29,35,41,.05)',
          zIndex: 10
        }}
        width={220}
      >
        <div style={{ 
          height: '64px', 
          margin: '0', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <span style={{ 
            fontSize: collapsed ? '16px' : '20px', 
            fontWeight: 'bold', 
            color: '#1890ff',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}>
            {collapsed ? 'HR' : 'HONGREN CLOUD'}
          </span>
        </div>
        <nav aria-label="主导航菜单">
          <Menu 
            theme="light" 
            defaultSelectedKeys={[location.pathname]} 
            selectedKeys={[location.pathname]}
            mode="inline" 
            items={menuItems} 
            onClick={handleMenuClick} 
            style={{ borderRight: 0, marginTop: '8px' }}
            role="navigation"
          />
        </nav>
      </Sider>
      <Layout>
        <Header style={{ 
          padding: '0 24px', 
          background: '#fff', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          zIndex: 9,
          height: '64px'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: '16px', width: 64, height: 64, marginLeft: -24 }}
          />
          
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer' }}>
              <Avatar style={{ backgroundColor: '#1890ff' }} icon={<UserOutlined />} />
              <span style={{ color: '#333', fontWeight: 500 }}>{userName || 'User'}</span>
              <DownOutlined style={{ fontSize: '12px', color: '#999' }} />
            </Space>
          </Dropdown>
        </Header>
        <Content style={{ 
            margin: '24px', 
            minHeight: 280, 
            background: 'transparent',
            overflow: 'initial'
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
